import type { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, Outlet } from 'react-router-dom';

import { faWarning } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';

import { BACKEND_URL_LIST } from '@spacefill/shared/src/constants/Backend';
import { darken } from '@spacefill/shared/src/utils/Color';
import { getEnvironmentVariable } from '@spacefill/shared/src/utils/GetEnvVar';
import { useBrandPreviewContext } from '@spacefill/shared/src/utils/contexts/BrandContext';
import { useSideBarContext } from '@spacefill/shared/src/utils/contexts/SideBarContext';
import { useTopBannerContext } from '@spacefill/shared/src/utils/contexts/TopBannerContext';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';

import type { ActionBarProps } from '../ActionBar/ActionBar';
import type { BreadcrumbProps } from '../Breadcrumb';
import { Button } from '../Button';
import type { SidebarProps } from '../Sidebar';

export interface AppLayoutProps {
    pageId?: string;
    dataTestid?: string;
    children: ReactNode[];
    leftNav?: ReactElement<SidebarProps>;
    topbar?: ReactElement<BreadcrumbProps>;
    actionBar?: ReactElement<ActionBarProps>;
    headerContent?: ReactNode;
}

const AppLayout = ({ pageId, dataTestid, leftNav, topbar, headerContent }: AppLayoutProps) => {
    const backendUrl = getEnvironmentVariable('backendUrl');
    const envName = getEnvironmentVariable('envName');

    const { t } = useTranslation();
    const { user } = useUserContext();
    const { hasTopBanner, topBanner } = useTopBannerContext();
    const { isSizeBarCollapsed = false } = useSideBarContext();
    const { brandPreview, clearBrandingPreview } = useBrandPreviewContext();

    const isProd = envName === 'sandbox' || envName === 'production';
    const showWarningMessage = backendUrl === BACKEND_URL_LIST.PRODUCTION && (!isProd || user.isStaff);

    const appBranding = {
        ...(user?.branding || {}),
        ...brandPreview,
    };

    return (
        <>
            {Object.keys(appBranding).length > 0 && (
                <style
                    type='text/css'
                    rel='stylesheet'
                >
                    {`
                    @layer base {
                        html {
                          ${appBranding.sidebarBackgroundColor ? `--color-sidebar-background: ${appBranding.sidebarBackgroundColor};` : ''}
                          ${appBranding.sidebarForegroundColor ? `--color-sidebar-foreground: ${appBranding.sidebarForegroundColor};` : ''}
                          ${appBranding.buttonBackgroundColor ? `--color-button-background: ${appBranding.buttonBackgroundColor};` : ''}
                          ${appBranding.buttonBackgroundColor ? `--color-button-background-hover: ${darken(appBranding.buttonBackgroundColor, 0.1)};` : ''}
                          ${appBranding.buttonBackgroundColor ? `--color-button-background-active: ${darken(appBranding.buttonBackgroundColor, 0.2)};` : ''}
                          ${appBranding.buttonBackgroundColor ? `--color-button-background-disabled: ${appBranding.buttonBackgroundColor}44;` : ''}
                          ${appBranding.buttonForegroundColor ? `--color-button-foreground: ${appBranding.buttonForegroundColor};` : ''}
                        }
                    }
                `}
                </style>
            )}

            <div
                className='flex h-full min-h-0 grow flex-col overflow-hidden'
                data-component-name='AppLayout'
            >
                {headerContent && (
                    <div
                        id='layout-header-content'
                        className='w-full'
                    >
                        {headerContent}
                    </div>
                )}

                <div
                    id='layout-viewport'
                    className='flex min-h-0 grow'
                >
                    {leftNav && (
                        <aside
                            id='layout-left-side'
                            className={clsx(
                                `relative shrink-0 transition-all`,
                                isSizeBarCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar-expanded'
                            )}
                        >
                            {leftNav}
                        </aside>
                    )}
                    <div className='group flex max-w-full min-h-0 grow flex-col overflow-hidden'>
                        <div className='relative flex min-h-0 grow flex-col'>
                            {topbar && <div className='absolute top-0 left-0 w-full'>{topbar}</div>}

                            <main
                                id={pageId}
                                data-testid={dataTestid}
                                className='grow min-h-0 px-6 pt-8 pb-20 group-has-[#app-layout-small-padding-bottom]:pb-8 mt-12 bg-gray-50 overflow-y-auto'
                            >
                                <header
                                    id='layout-header'
                                    className='-mx-6 -mt-8 pb-2 mb-2'
                                >
                                    {showWarningMessage && (
                                        <div className='flex h-10 w-full items-center justify-center bg-red-500'>
                                            <p className='text-center text-white'>
                                                ⚠️ Warning: You are currently interacting with
                                                {backendUrl === BACKEND_URL_LIST.PRODUCTION
                                                    ? ' production'
                                                    : ' sandbox'}{' '}
                                                data, not a local environment. Please proceed with caution.
                                            </p>
                                        </div>
                                    )}
                                    {brandPreview && (
                                        <div className='flex p-2 w-full items-center justify-center bg-orange-500'>
                                            <div className='text-center text-white grow'>
                                                <FontAwesomeIcon
                                                    icon={faWarning}
                                                    className='mr-2'
                                                />
                                                {t('You are browsing with a preview of customized branding.')}
                                            </div>
                                            <div className='ml-auto flex gap-2 items-center'>
                                                <Button
                                                    small
                                                    variant='outlined'
                                                    type='button'
                                                    onClick={() => clearBrandingPreview()}
                                                >
                                                    {t('Stop preview')}
                                                </Button>
                                                <Button
                                                    small
                                                    variant='outlined'
                                                    type='link'
                                                    to='/settings/display'
                                                >
                                                    {t('Go to settings')}
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {hasTopBanner && (
                                        <Link
                                            id='notification-drawer'
                                            to={topBanner.link || '#'}
                                            onClick={
                                                typeof topBanner.onClick === 'function' ? topBanner.onClick : undefined
                                            }
                                            data-testid={topBanner.id}
                                            className='flex h-4 items-center justify-center border-none bg-sky-400 py-6 text-sm text-gray-50 no-underline outline-hidden hover:no-underline focus:no-underline'
                                        >
                                            {topBanner.text}
                                        </Link>
                                    )}
                                </header>
                                <Outlet />
                            </main>
                        </div>
                        <div
                            className={clsx(
                                'fixed bottom-0 right-0 transition-all',
                                isSizeBarCollapsed ? 'left-sidebar-collapsed' : 'left-sidebar-expanded'
                            )}
                            id='app-layout-footer'
                        />
                    </div>

                    <div id='app-layout-sidepanel' />
                </div>
            </div>
        </>
    );
};

const AppLayoutFooter = ({ children }: PropsWithChildren) => {
    const mountPoint = document.getElementById('app-layout-footer');
    if (mountPoint) {
        return createPortal(<div id='app-layout-footer-content'>{children}</div>, mountPoint);
    }
};

export { AppLayout, AppLayoutFooter };
